import React, { useCallback, useEffect, useMemo, useState } from 'react';

export default function NurseHome({
  token,
  adminName,
  adminRole,
  onGoToIntake,
  onGoToMeds,
  onGoToIncidents
}) {
  const [nurseDashLoading, setNurseDashLoading] = useState(false);
  const [nurseDashError, setNurseDashError] = useState('');
  const [nurseDashRows, setNurseDashRows] = useState([]);

  const fetchNurseDashboard = useCallback(async (t) => {
    const authToken = t || '';
    if (!authToken) return;

    setNurseDashError('');
    setNurseDashLoading(true);

    try {
      // IMPORTANT: singular endpoint (matches NurseIntake.jsx comment)
      const res = await fetch(`/.netlify/functions/admin-get-nurse-registration?ts=${Date.now()}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw };
      }

      if (!res.ok) {
        setNurseDashRows([]);
        setNurseDashError(json?.error || `Failed to load nurse dashboard: ${res.status}`);
        return;
      }

      setNurseDashRows(Array.isArray(json.rows) ? json.rows : []);
    } catch (e) {
      setNurseDashRows([]);
      setNurseDashError('Network error while loading nurse dashboard');
    } finally {
      setNurseDashLoading(false);
    }
  }, []);

  // auto-load on mount (when nurse logs in and home renders)
  useEffect(() => {
    if (!token) return;
    fetchNurseDashboard(token);
  }, [token, fetchNurseDashboard]);

  const stats = useMemo(() => {
    const rows = Array.isArray(nurseDashRows) ? nurseDashRows : [];
    const norm = (v) => String(v || '').toLowerCase();

    const allergiesText = (r) => norm(r.Allergies || r.Alllergies).trim();
    const medsText = (r) => norm(r.Medications).trim();

    const allergyAny = rows.filter((r) => allergiesText(r)).length;
    const medsAny = rows.filter((r) => medsText(r)).length;
    const notReviewed = rows.filter((r) => !r.reviewed).length;

    const countByKeyword = (kwList) =>
      rows.filter((r) => {
        const a = allergiesText(r);
        return kwList.some((kw) => a.includes(kw));
      }).length;

    return {
      total: rows.length,
      notReviewed,
      allergyAny,
      medsAny,
      nut: countByKeyword(['nut', 'peanut', 'tree nut']),
      shellfish: countByKeyword(['shellfish']),
      dairy: countByKeyword(['dairy', 'milk']),
      egg: countByKeyword(['egg']),
      gluten: countByKeyword(['gluten', 'wheat'])
    };
  }, [nurseDashRows]);

  const jumpToIntake = (prefill) => {
    if (typeof onGoToIntake === 'function') onGoToIntake(prefill);
  };

  return (
    <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'baseline'
          }}
        >
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 4 }}>Nurse Home</h2>
            <div className="muted">Overview of allergies/meds and quick links.</div>
          </div>

          <button className="btn" onClick={() => fetchNurseDashboard(token)} disabled={nurseDashLoading}>
            {nurseDashLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {nurseDashError ? (
          <div className="error" style={{ marginTop: 10 }}>
            {nurseDashError}
          </div>
        ) : null}

        {/* Symmetric layout: left stacked (Needs review + Any meds), right allergy breakdown */}
        <div
          style={{
            marginTop: 12,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 10,
            alignItems: 'stretch'
          }}
        >
          {/* Left column: stacked cards */}
          <div style={{ display: 'grid', gap: 10 }}>
            {/* Needs review */}
            <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Needs review
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, marginTop: 6 }}>{stats.notReviewed}</div>
            </div>

            {/* Any meds */}
            <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Any meds
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, marginTop: 6 }}>{stats.medsAny}</div>
            </div>
          </div>

          {/* Right column: Allergy breakdown (clickable) */}
          <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                Allergy breakdown
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Total: {stats.allergyAny}
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
              <button
                type="button"
                className="btn"
                style={{ justifyContent: 'space-between', display: 'flex' }}
                onClick={() => jumpToIntake('nut')}
              >
                <span>Nuts</span>
                <span style={{ fontWeight: 900 }}>{stats.nut}</span>
              </button>

              <button
                type="button"
                className="btn"
                style={{ justifyContent: 'space-between', display: 'flex' }}
                onClick={() => jumpToIntake('shellfish')}
              >
                <span>Shellfish</span>
                <span style={{ fontWeight: 900 }}>{stats.shellfish}</span>
              </button>

              <button
                type="button"
                className="btn"
                style={{ justifyContent: 'space-between', display: 'flex' }}
                onClick={() => jumpToIntake('milk')}
              >
                <span>Dairy / Milk</span>
                <span style={{ fontWeight: 900 }}>{stats.dairy}</span>
              </button>

              <button
                type="button"
                className="btn"
                style={{ justifyContent: 'space-between', display: 'flex' }}
                onClick={() => jumpToIntake('egg')}
              >
                <span>Egg</span>
                <span style={{ fontWeight: 900 }}>{stats.egg}</span>
              </button>

              <button
                type="button"
                className="btn"
                style={{ justifyContent: 'space-between', display: 'flex' }}
                onClick={() => jumpToIntake('gluten')}
              >
                <span>Gluten / Wheat</span>
                <span style={{ fontWeight: 900 }}>{stats.gluten}</span>
              </button>
            </div>

            <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
              Tap a row to jump to “From registrations” with the search filled in.
            </div>
          </div>
        </div>

        {/* Buttons removed (use hamburger tabs or click allergy rows) */}

        <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
          Logged in as: <strong>{adminName}</strong>
          {adminRole ? ` (${adminRole})` : ''}
        </div>
      </div>
    </div>
  );
}